import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { baseStyles } from '../styles/shared.js';
import { ACTIVITY_BAR_ITEMS, ACTIVITY_BAR_WIDTH } from '../utils/constants.js';
import type { ActivityBarItem } from '../utils/constants.js';

// ---------------------------------------------------------------------------
// <sysml-activity-bar> — 48px vertical icon rail (VS Code-style)
//
// Top section: context-switching icons (explorer, search, model tree)
// Bottom section: settings gear
// ---------------------------------------------------------------------------

@customElement('sysml-activity-bar')
export class SysmlActivityBar extends LitElement {
  static override styles = [
    baseStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        width: ${ACTIVITY_BAR_WIDTH}px;
        flex-shrink: 0;
        background: var(--r5-surface-muted);
        border-right: 1px solid var(--r5-border);
        overflow: visible;
        position: relative;
        z-index: 2;
      }
      .top {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        padding-top: var(--r5-space-1);
      }
      .bottom {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-bottom: var(--r5-space-2);
      }
      .ab-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border: none;
        border-radius: var(--r5-radius-md);
        background: none;
        color: var(--r5-text-muted);
        cursor: pointer;
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
        margin-bottom: 2px;
      }
      .ab-btn:hover {
        color: var(--r5-text);
        background: color-mix(in srgb, var(--r5-text) 8%, transparent);
      }
      .ab-btn.active {
        color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 12%, transparent);
      }
    `,
  ];

  @property({ attribute: false }) activeItem: ActivityBarItem | null = 'explorer';
  @property({ type: Boolean, attribute: 'sidebar-collapsed' }) sidebarCollapsed = false;

  override render() {
    return html`
      <div class="top" role="toolbar" aria-label="Activity Bar" aria-orientation="vertical">
        ${ACTIVITY_BAR_ITEMS.map(
          (item) => html`
            <ark-tooltip content=${item.label} placement="right">
              <button
                class="ab-btn ${this.activeItem === item.id && !this.sidebarCollapsed ? 'active' : ''}"
                aria-label=${item.label}
                aria-pressed=${this.activeItem === item.id && !this.sidebarCollapsed ? 'true' : 'false'}
                @click=${() => this._onItemClick(item.id)}
              >
                <ark-icon name=${item.icon} size="md"></ark-icon>
              </button>
            </ark-tooltip>
          `,
        )}
      </div>
      <div class="bottom">
        <ark-tooltip content="Settings" placement="right">
          <button class="ab-btn" aria-label="Settings" @click=${this._onSettings}>
            <ark-icon name="settings" size="md"></ark-icon>
          </button>
        </ark-tooltip>
      </div>
    `;
  }

  private _onItemClick(item: ActivityBarItem): void {
    this.dispatchEvent(
      new CustomEvent('activity-item-click', {
        detail: { item },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onSettings(): void {
    this.dispatchEvent(
      new CustomEvent('settings-click', {
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-activity-bar': SysmlActivityBar;
  }
}
